import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Landing from '@/pages/Landing';

// Lazy-loaded pages — each becomes a separate JS chunk
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const UploadCenter = lazy(() => import('@/pages/UploadCenter'));
const KnowledgeLibrary = lazy(() => import('@/pages/KnowledgeLibrary'));
const QuizArena = lazy(() => import('@/pages/QuizArena'));
const BossBattleArena = lazy(() => import('@/pages/BossBattleArena'));
const SmartNotes = lazy(() => import('@/pages/SmartNotes'));
const MaterialViewer = lazy(() => import('@/pages/MaterialViewer'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));

// Platform Admin
const AdminDashboard = lazy(() => import('@/pages/Admin/AdminDashboard'));
const UserDirectory = lazy(() => import('@/pages/Admin/UserDirectory'));
const UserDetail = lazy(() => import('@/pages/Admin/UserDetail'));
const SystemSettings = lazy(() => import('@/pages/Admin/SystemSettings'));
const QueueMonitor = lazy(() => import('@/pages/Admin/QueueMonitor'));
const FeedbackQuality = lazy(() => import('@/pages/Admin/FeedbackQuality'));
const Experiments = lazy(() => import('@/pages/Admin/Experiments'));
import Layout from '@/components/Layout';
import { GoogleOAuthProvider } from '@react-oauth/google';
import CookieConsent from '@/components/CookieConsent';

import { fetchCsrfToken } from '@/lib/api';
import { ProcessingNotificationsProvider } from '@/contexts/ProcessingNotificationsContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else if (response.status === 401) {
        logout();
      } else {
        console.warn('Profile fetch failed with status:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  }, [token, logout]);

  useEffect(() => {
    // Initialize CSRF
    fetchCsrfToken();
  }, []);

  useEffect(() => {
    if (token) {
      fetchUserProfile();
    }
  }, [token, fetchUserProfile]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const refreshUser = () => {
    fetchUserProfile();
  };

  if (!token) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Landing onLogin={login} />
        <CookieConsent />
      </GoogleOAuthProvider>
    );
  }

  return (
    <BrowserRouter>
      <ProcessingNotificationsProvider>
        <Layout user={user} onLogout={logout} refreshUser={refreshUser}>
          <Suspense fallback={
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
            </div>
          }>
            <Routes>
              <Route path="/" element={<Dashboard user={user} />} />
              <Route path="/upload" element={<UploadCenter />} />
              <Route path="/library" element={<KnowledgeLibrary />} />
              <Route path="/quiz/:materialId" element={<QuizArena user={user} refreshUser={refreshUser} />} />
              <Route path="/course-exam/:materialId" element={<QuizArena user={user} refreshUser={refreshUser} />} />
              <Route path="/boss-battle/:materialId" element={<BossBattleArena user={user} refreshUser={refreshUser} />} />
              <Route path="/notes/:materialId" element={<SmartNotes />} />
              <Route path="/material/:materialId/view" element={<MaterialViewer />} />
              <Route path="/analytics" element={<Analytics user={user} />} />
              <Route path="/settings/billing" element={<BillingPage />} />


              {/* Admin Routes — guarded by role, waits for user profile to load */}
              {user?.role === 'ADMIN' ? (
                <>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/users" element={<UserDirectory />} />
                  <Route path="/admin/users/:userId" element={<UserDetail />} />
                  <Route path="/admin/settings" element={<SystemSettings />} />
                  <Route path="/admin/queue" element={<QueueMonitor />} />
                  <Route path="/admin/feedback" element={<FeedbackQuality />} />
                  <Route path="/admin/experiments" element={<Experiments />} />
                </>
              ) : user === null ? (
                <Route path="/admin/*" element={
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
                  </div>
                } />
              ) : (
                <Route path="/admin/*" element={<Navigate to="/" replace />} />
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Layout>
      </ProcessingNotificationsProvider>
      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;